import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import FilterAltOffIcon from '@material-design-icons/svg/filled/filter_alt_off.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import SearchIcon from '@material-design-icons/svg/filled/search.svg?react';
import SwapVertIcon from '@material-design-icons/svg/filled/swap_vert.svg?react';
import TuneIcon from '@material-design-icons/svg/filled/tune.svg?react';
import type { SortingState } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import type {
  ColumnVisibilityState,
  HubspotColumnKey,
} from '../../../lib/hubspot-management/columnVisibility';
import {
  getHubspotSortKey,
  hubspotRequestTypeOptions,
  hubspotSortOptions,
  hubspotSourceOptions,
} from '../../../lib/hubspot-management/options';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { HubspotColumnVisibilityMenu } from '../filters/HubspotColumnVisibilityMenu';

export interface HubspotTicketFilters {
  pipeline: string;
  requestType: string;
  source: string;
  assignee: string;
  search: string;
}

export type HubspotTicketFilterKey = Exclude<
  keyof HubspotTicketFilters,
  'search'
>;

interface HubspotTicketsToolbarProps {
  filters: HubspotTicketFilters;
  viewerUserId: string;
  pipelineStageOptions: Array<{ value: string; label: string }>;
  assigneeOptions: Array<{ value: string; label: string }>;
  sorting: SortingState;
  columnVisibility: ColumnVisibilityState;
  /** Narrow layouts (phones, or the navigator beside an open ticket). */
  compact: boolean;
  showColumnMenu: boolean;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: HubspotTicketFilterKey, value: string) => void;
  onSortingChange: (sorting: SortingState) => void;
  onColumnVisibilityChange: (key: HubspotColumnKey, checked: boolean) => void;
  onClearFilters: () => void;
}

const UNASSIGNED_FILTER_VALUE = '_unassigned';

const controlClassName =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 font-medium text-neutral-300 transition-colors hover:border-neutral-700 hover:bg-neutral-800 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500';

function ToolbarSelect({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  className?: string;
}) {
  const isActive = value !== '';

  return (
    <div className={cn('relative min-w-0', className)}>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'h-9 w-full cursor-pointer appearance-none rounded-lg border bg-neutral-900 pl-3 pr-8 transition-colors focus:outline-none focus:ring-1 focus:ring-teal-500',
          isActive
            ? 'border-teal-500/40 text-teal-100'
            : 'border-neutral-800 text-neutral-300 hover:border-neutral-700',
        )}
        style={{ colorScheme: 'dark' }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <KeyboardArrowDownIcon className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 fill-current text-neutral-500" />
    </div>
  );
}

function TicketSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebounce(localValue, 400);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    setLocalValue(value);
    lastEmittedRef.current = value;
  }, [value]);

  useEffect(() => {
    if (debouncedValue !== lastEmittedRef.current) {
      lastEmittedRef.current = debouncedValue;
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange]);

  return (
    <div className="relative">
      <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 fill-current text-neutral-500" />
      <input
        type="text"
        aria-label="Search tickets"
        placeholder="Search name, package, ticket…"
        value={localValue}
        onChange={(event) => setLocalValue(event.target.value)}
        className="h-9 w-full rounded-lg border border-neutral-800 bg-neutral-900 pl-8 pr-8 text-white placeholder-neutral-500 transition-colors hover:border-neutral-700 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
      />
      {localValue && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setLocalValue('');
            lastEmittedRef.current = '';
            onChange('');
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-500 transition-colors hover:text-white"
        >
          <CloseIcon className="size-3.5 fill-current" />
        </button>
      )}
    </div>
  );
}

function SortMenu({
  sorting,
  onSortingChange,
  className,
}: {
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  className?: string;
}) {
  const primary = sorting[0];
  const currentKey = primary
    ? getHubspotSortKey(primary.id, primary.desc)
    : getHubspotSortKey('createdAt', true);
  const current = hubspotSortOptions.find(
    (option) => getHubspotSortKey(option.id, option.desc) === currentKey,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(controlClassName, className)}
          aria-label="Sort tickets"
        >
          <SwapVertIcon className="size-4 fill-current text-neutral-400" />
          <span className="truncate">{current?.label ?? 'Sort'}</span>
          <KeyboardArrowDownIcon className="ml-auto size-4 shrink-0 fill-current text-neutral-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-52 rounded-lg border border-neutral-700 bg-neutral-900 p-1 text-white"
      >
        <DropdownMenuRadioGroup
          value={currentKey}
          onValueChange={(value) => {
            const [id, direction] = value.split(':');
            onSortingChange([{ id, desc: direction === 'desc' }]);
          }}
        >
          {hubspotSortOptions.map((option) => (
            <DropdownMenuRadioItem
              key={getHubspotSortKey(option.id, option.desc)}
              value={getHubspotSortKey(option.id, option.desc)}
              className="cursor-pointer rounded text-sm text-neutral-200 focus:bg-neutral-800 focus:text-white"
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function HubspotTicketsToolbar({
  filters,
  viewerUserId,
  pipelineStageOptions,
  assigneeOptions,
  sorting,
  columnVisibility,
  compact,
  showColumnMenu,
  onSearchChange,
  onFilterChange,
  onSortingChange,
  onColumnVisibilityChange,
  onClearFilters,
}: HubspotTicketsToolbarProps) {
  const segments = [
    { key: 'all', label: 'All', assignee: '' },
    { key: 'mine', label: 'Assigned to me', assignee: viewerUserId },
    {
      key: 'unassigned',
      label: 'Unassigned',
      assignee: UNASSIGNED_FILTER_VALUE,
    },
  ];
  const isSegmentAssignee = segments.some(
    (segment) => segment.assignee === filters.assignee,
  );
  const activeFilterCount =
    [filters.pipeline, filters.requestType, filters.source].filter(Boolean)
      .length + (isSegmentAssignee ? 0 : 1);
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(
    activeFilterCount > 0,
  );

  const filterSelects = (
    <>
      <ToolbarSelect
        label="Stage"
        value={filters.pipeline}
        options={pipelineStageOptions}
        onChange={(value) => onFilterChange('pipeline', value)}
      />
      <ToolbarSelect
        label="Request type"
        value={filters.requestType}
        options={hubspotRequestTypeOptions}
        onChange={(value) => onFilterChange('requestType', value)}
      />
      <ToolbarSelect
        label="Source"
        value={filters.source}
        options={hubspotSourceOptions}
        onChange={(value) => onFilterChange('source', value)}
      />
      <ToolbarSelect
        label="Assignee"
        value={filters.assignee}
        options={assigneeOptions}
        onChange={(value) => onFilterChange('assignee', value)}
      />
    </>
  );

  const clearButton = hasActiveFilters ? (
    <button
      type="button"
      onClick={onClearFilters}
      className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
    >
      <FilterAltOffIcon className="size-4 fill-current" />
      Clear
    </button>
  ) : null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Queue segment"
          className="flex max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-lg bg-neutral-900 p-0.5 ring-1 ring-neutral-800 scrollbar-hide"
        >
          {segments.map((segment) => {
            const isActive = filters.assignee === segment.assignee;

            return (
              <button
                key={segment.key}
                type="button"
                aria-pressed={isActive}
                onClick={() => onFilterChange('assignee', segment.assignee)}
                className={cn(
                  'whitespace-nowrap rounded-md px-3 py-1.5 font-medium transition-colors',
                  isActive
                    ? 'bg-neutral-800 text-white shadow-sm'
                    : 'text-neutral-400 hover:text-neutral-200',
                )}
              >
                {segment.label}
              </button>
            );
          })}
        </div>

        <div className="min-w-[180px] flex-1 text-base sm:text-sm">
          <TicketSearch value={filters.search} onChange={onSearchChange} />
        </div>

        {compact ? (
          <button
            type="button"
            aria-expanded={isFilterPanelOpen}
            onClick={() => setIsFilterPanelOpen((open) => !open)}
            className={cn(
              controlClassName,
              isFilterPanelOpen &&
                'border-neutral-700 bg-neutral-800 text-white',
            )}
          >
            <TuneIcon className="size-4 fill-current text-neutral-400" />
            Filters
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-teal-500/20 px-1.5 text-[11px] leading-4 text-teal-200">
                {activeFilterCount}
              </span>
            )}
          </button>
        ) : (
          <>
            {filterSelects}
            <SortMenu sorting={sorting} onSortingChange={onSortingChange} />
            {showColumnMenu && (
              <HubspotColumnVisibilityMenu
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={onColumnVisibilityChange}
                triggerClassName={controlClassName}
              />
            )}
            {clearButton}
          </>
        )}
      </div>

      {compact && isFilterPanelOpen && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-neutral-800 bg-neutral-900/60 p-2 text-base sm:text-sm">
          {filterSelects}
          <SortMenu
            sorting={sorting}
            onSortingChange={onSortingChange}
            className="col-span-2 justify-start"
          />
          {clearButton && <div className="col-span-2 flex">{clearButton}</div>}
        </div>
      )}
    </div>
  );
}
